import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import styles from './ConfirmationModal.module.scss'

import Button from '../../Button/Button'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'

interface ConfirmationModalProps {
  title: string
  message: string
  onConfirm: () => void
  onClose: () => void
  isOpen: boolean
  disablePortal?: boolean
  /**
   * The reset exists for the "discard unsaved changes" use, where confirming
   * abandons the form. A confirmation about one field inside a form that stays
   * open must not clear it, or the form silently stops warning on close.
   */
  resetsFormDirty?: boolean
  /**
   * False for an action that can still be taken back, such as an edit undone
   * before it is saved: the "cannot be undone" line would not be true there.
   */
  irreversible?: boolean
}

function ConfirmationModal(props: ConfirmationModalProps) {
  const {
    title,
    message,
    onConfirm,
    onClose,
    isOpen,
    disablePortal,
    resetsFormDirty = true,
    irreversible = true
  } = props
  const { t } = useTranslation()
  const resetIsDirty = useFormDirtyStore(s => s.reset)

  const handleOnConfirm = () => {
    if (resetsFormDirty) {
      resetIsDirty()
    }
    onConfirm()
  }
  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      disablePortal={disablePortal}
    >
      <div className={styles.confirmationContainer}>
        <p className={`body-medium ${styles.confirmationSubHeader}`}>
          {message}
        </p>
        {irreversible && (
          <p className={`body-medium ${styles.confirmationSubHeader}`}>
            {t('CONFIRMATION.SUB_TITLE.FIRST_PART')}
            <mark className="mark-orange">
              {t('CONFIRMATION.SUB_TITLE.SECOND_PART')}
            </mark>
            {t('CONFIRMATION.SUB_TITLE.THIRD_PART')}
          </p>
        )}
        <div className={styles.confirmationButtonContainer}>
          <Button
            color="primary"
            label={t('CONFIRMATION.CONFIRM')}
            variant="contained"
            onClick={handleOnConfirm}
          />
          <Button
            color="secondary"
            label={t('CONFIRMATION.CANCEL')}
            variant="contained"
            onClick={onClose}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmationModal
